import { useState, useEffect } from 'react'
import axios from 'axios'
import Header from '../components/shared/Header'
import Footer from '../components/shared/Footer'
import MetaTags from '../components/shared/MetaTags'
import StructuredData from '../components/shared/StructuredData'

export default function BlogPage() {
  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchPublications = async () => {
      try {
        const response = await axios.get('/api/v1/research')
        setPosts(response.data)
      } catch (error) {
        console.error('Failed to fetch observations:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchPublications()
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-dark text-text-dark">
        <div className="max-w-container mx-auto px-5 md:px-8 py-8">
          <Header />
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-muted-dark">Loading research findings...</div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <MetaTags
        title="Observations | deedslab.com"
        description="Experimental observations, strategic insights, and methodological reflections from ongoing investigations. Explore the iterative learnings and discoveries from the laboratory."
        url="/notes"
        type="website"
      />
      <StructuredData
        type="Blog"
        title="Observations | deedslab.com"
        description="Experimental observations, strategic insights, and methodological reflections from ongoing investigations."
        url="/notes"
      />

      <div className="min-h-screen bg-bg-dark text-text-dark">
        <div className="max-w-container mx-auto px-5 md:px-8 py-8">
          <Header />

          {/* Hero Section */}
          <section className="py-12 md:py-16">
            <div className="max-w-4xl mx-auto text-center">
              <div className="font-mono text-xs tracking-wide uppercase text-accent-dark mb-4">
                Observations
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-accent-dark to-accent-light bg-clip-text text-transparent">
                Observations
              </h1>
              <p className="text-xl text-muted-dark leading-relaxed max-w-2xl mx-auto">
                Experimental observations, strategic insights, and methodological reflections from ongoing investigations.
                Each observation represents iterative learnings from controlled experimental protocols.
              </p>
            </div>
          </section>

          {/* Posts Grid */}
          <section className="py-8 md:py-12">
            {posts.length === 0 ? (
              <div className="text-center py-16">
                <div className="font-mono text-sm text-muted-dark mb-4">
                  No observations yet
                </div>
                <p className="text-muted-dark">
                  Research findings will be published here as experiments conclude and analysis is completed.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {posts.map((post) => (
                  <article
                    key={post.id}
                    className="border border-border-dark rounded-large p-6 bg-surface-dark-1 hover:border-accent-dark/50 transition-colors group"
                  >
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                      <span className="font-mono text-xs tracking-wide uppercase text-accent-dark bg-accent-dark/10 px-2 py-1 rounded">
                        Observation
                      </span>
                      {post.research_area && (
                        <span className="font-mono text-xs tracking-wide uppercase text-accent-light bg-accent-light/10 px-2 py-1 rounded">
                          {post.research_area}
                        </span>
                      )}
                      {post.methodology && (
                        <span className="font-mono text-xs tracking-wide uppercase text-muted-dark bg-surface-dark-2 px-2 py-1 rounded">
                          {post.methodology}
                        </span>
                      )}
                      {post.validation_status && (
                        <span className={`font-mono text-xs tracking-wide uppercase px-2 py-1 rounded ${
                          post.validation_status === 'validated'
                            ? 'text-green-400 bg-green-400/10'
                            : post.validation_status === 'peer-reviewed'
                            ? 'text-blue-400 bg-blue-400/10'
                            : 'text-yellow-400 bg-yellow-400/10'
                        }`}>
                          {post.validation_status}
                        </span>
                      )}
                    </div>

                    <h2 className="text-lg font-semibold mb-3 text-text-dark group-hover:text-accent-dark transition-colors">
                      <a href={`/research/${post.slug}`} className="hover:underline">
                        {post.title}
                      </a>
                    </h2>

                    <p className="text-sm text-muted-dark mb-4 leading-relaxed line-clamp-3">
                      {post.excerpt}
                    </p>

                    <div className="flex items-center justify-between text-xs text-muted-dark font-mono">
                      <span>By {post.author}</span>
                      {post.reading_time && (
                        <span>{post.reading_time} min read</span>
                      )}
                    </div>

                    {post.published_at && (
                      <div className="mt-3 text-xs text-muted-dark">
                        Published {new Date(post.published_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </div>
                    )}

                    <div className="mt-4">
                      <a
                        href={`/notes/${post.slug}`}
                        className="inline-flex items-center gap-2 text-accent-dark hover:underline text-sm font-medium"
                      >
                        <span>Read Note</span>
                        <span className="text-xs">→</span>
                      </a>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>

          <Footer />
        </div>
      </div>
    </>
  )
}
